import { useMutation } from "@apollo/client";
import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import useTypedContext from "hooks/useTypedContext";
import { AccessResult } from "utils/access";
import Tooltip from "ds/components/Tooltip";

import { useRunShortcut } from "../../hooks/useRunShortcut";
import { STOP_RUN } from "./gql";
type StopRunButtonProps = {
  runId: string;
  stackId: string;
  runQueryToRefetch?: string;
  canStop: AccessResult;
};

const StopRunButton = ({ runId, stackId, runQueryToRefetch, canStop }: StopRunButtonProps) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const [stopRuns, { loading, data }] = useMutation(STOP_RUN, {
    variables: { stackId, runId },
    refetchQueries: [runQueryToRefetch || "GetRun"],
    awaitRefetchQueries: true,
  });

  const onClick = useCallback(() => {
    stopRuns()
      .then(({ data }) => {
        if (data?.runStop?.id) {
          reportSuccess({ message: `Run ${data.runStop.id} successfully stopped` });
        }
      })
      .catch(onError);
  }, [stopRuns, onError, reportSuccess]);

  const isLoading = loading || !!data?.runStop?.id;

  useRunShortcut({
    label: "Stop",
    code: "mod+alt+s",
    callback: onClick,
    loading: isLoading,
    disabled: !canStop.result,
  });

  if (!canStop.result && !canStop.message) {
    return null;
  }

  return (
    <Tooltip
      active={!!canStop.message}
      on={({ ...props }) => (
        <Button
          {...props}
          variant="secondary"
          disabled={isLoading || !canStop.result}
          loading={isLoading}
          onPress={onClick}
          size="small"
        >
          Stop
        </Button>
      )}
    >
      {canStop.message}
    </Tooltip>
  );
};

export default StopRunButton;
